import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Video, BookOpen, Brain, Award, Users, CheckCircle, GraduationCap, Building, Target } from 'lucide-react';
import { courses, testimonials } from '../data/mock';
import SEO from '../components/SEO';
import VittaPathways from '../components/VittaPathways';

const Home = () => {
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Vitta Finance Academy",
    "alternateName": "Vitta Finance CIRO Exam Prep",
    "url": "https://www.vittafinance.ca",
    "description": "Canada's #1 CIRO exam prep provider. Pass CIRE, RSE, ISE, and Derivatives exams.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.vittafinance.ca/courses?search={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "EducationalOrganization",
      "name": "Vitta Finance Academy",
      "logo": "https://www.vittafinance.ca/logo.jpg"
    }
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="Vitta Finance Academy - CIRO Exam Prep Canada | CIRE, RSE, ISE, Derivatives Courses"
        description="Pass your CIRO exams with Canada's #1 exam prep provider. Expert-led CIRE, RSE, ISE & Derivatives courses. Industry leading pass rate, 30,000+ successful learners. AI tutor, mock exams, 700+ page study guides. Serving Alberta, Ontario, BC & all Canadian provinces."
        keywords="CIRO exam, CIRO exams, CIRO proficiency exam, CIRO proficiency exams, CIRO new proficiency model, CIRO proficiency model 2026, CIRO licensing exam, CIRO registration exam, Canadian Investment Regulatory Organization exam, investment dealer licensing Canada, securities licensing Canada, become registered representative Canada, CIRO exam hub, CIRO exam syllabus, CIRO practice exam, CIRO study guide, CIRE exam, CIRE exam prep, pass CIRE exam, RSE exam, Retail Securities Exam, ISE exam, Institutional Securities Exam, Derivatives exam, IIROC exam, MFDA exam, CIRO vs IIROC, CIRO vs MFDA, CIRO exam prep course, CIRO exam prep provider, Canadian securities licensing course, investment advisor licensing Canada, CIRO exam prep Canada, CIRO exam prep Alberta, CIRO exam prep Ontario, CIRO exam prep Toronto, CIRO exam prep Vancouver"
        ogTitle="Vitta Finance Academy - Canada's #1 CIRO Exam Prep Provider"
        ogDescription="Pass your CIRO exams with confidence. Expert-led courses for CIRE, RSE, ISE & Derivatives. Industry leading pass rate. Trusted by 30,000+ Canadian finance professionals."
        structuredData={homeStructuredData}
        pageType="website"
      />
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '120px 0 80px',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.95), rgba(59, 130, 246, 0.9))',
      }}>
        {/* Background Image */}
        <div style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          zIndex: 0,
          overflow: 'hidden'
        }}>
          <img 
            src="/images/backgrounds/hero-bg.jpg"
            alt="Professional Education"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.15
            }}
          />
        </div>

        {/* Hero Content */}
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '800px' }}>
            <h1 className="brand-display" style={{ marginBottom: '24px', color: 'white' }}>
              Master CIRO Exams with Confidence
            </h1>
            <p className="body-large" style={{ 
              marginBottom: '24px',
              color: 'rgba(255, 255, 255, 0.95)'
            }}>
              Complete CIRO Exam Prep — CIRE, Retail Securities, Institutional Securities and Derivatives
            </p>
            <p className="body-medium" style={{ 
              marginBottom: '40px',
              color: 'rgba(255, 255, 255, 0.85)'
            }}>
              Instructor Trusted by Over 30,000 Learners Across CSC, FRM, CFA and IFC Courses
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link to="/books" data-testid="hero-view-books-btn" className="hero-btn hero-btn-primary">
                <BookOpen size={20} />
                View Books
              </Link>
              <Link to="/courses" data-testid="hero-explore-courses-btn" className="hero-btn hero-btn-gold">
                <ArrowRight size={20} />
                Explore Courses
              </Link>
              <a href="https://vittafinance.mykajabi.com/login" target="_blank" rel="noopener noreferrer" data-testid="hero-member-login-btn" className="hero-btn hero-btn-white">
                <Users size={20} />
                Member Login
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Study Pathways Section */}
      <VittaPathways />

      {/* Foundational Pathway - Printed Books Section */}
      <section style={{
        padding: '64px 20px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '8px'
            }}>
              Foundational Pathway — Printed Books
            </h2>
            <p style={{
              fontSize: '1rem',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Start with our comprehensive printed study guides, then enhance with video courses
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '20px'
          }} className="foundational-grid">
            {[
              {
                id: 'cire',
                title: 'CIRE',
                fullName: 'Canadian Investment Regulatory Exam',
                color: '#1e40af',
                amazonLink: 'https://www.amazon.ca/dp/B0FV3J7PJJ',
                videoLink: 'https://vittafinance.mykajabi.com/offers/5Tzz73ay/checkout',
                videoStatus: 'active'
              },
              {
                id: 'rse',
                title: 'RSE',
                fullName: 'Retail Securities Exam',
                color: '#166534',
                amazonLink: 'https://a.co/d/060T5NBJ',
                videoLink: 'https://vittafinance.mykajabi.com/offers/2kzQbJim/checkout',
                videoStatus: 'active'
              },
              {
                id: 'derivatives',
                title: 'Derivatives',
                fullName: 'CIRO Derivatives Exam',
                color: '#92400e',
                amazonLink: 'https://a.co/d/071fIsJy',
                videoLink: 'https://vittafinance.mykajabi.com/offers/K52zhpSs/checkout',
                videoStatus: 'active'
              },
              {
                id: 'ise',
                title: 'ISE',
                fullName: 'Institutional Securities Exam',
                color: '#7c3aed',
                amazonLink: 'https://a.co/d/0cmr9k3i',
                videoLink: 'https://vittafinance.mykajabi.com/offers/W6ezafud/checkout',
                videoStatus: 'active'
              },
              {
                id: 'traders',
                title: 'Traders',
                fullName: 'CIRO Traders Licensing Exam',
                color: '#0d9488',
                amazonLink: 'https://a.co/d/0c400Tnw',
                videoLink: null,
                videoStatus: 'coming-soon',
                videoPrice: '$199'
              }
            ].map((exam) => (
              <div key={exam.id} style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px 20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                textAlign: 'center',
                position: 'relative'
              }}>
                {exam.videoStatus === 'coming-soon' && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                    color: 'white',
                    padding: '3px 10px',
                    borderRadius: '10px',
                    fontSize: '0.65rem',
                    fontWeight: '700'
                  }}>NEW</div>
                )}
                <h3 style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: exam.color,
                  marginBottom: '4px'
                }}>{exam.title}</h3>
                <p style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  marginBottom: '16px',
                  lineHeight: '1.3'
                }}>{exam.fullName}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <a
                    href={exam.amazonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${exam.id}-amazon-btn`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px 14px',
                      background: '#232f3e',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'background 0.2s'
                    }}
                  >
                    <BookOpen size={16} />
                    Amazon Book
                    <span style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}>$299.99</span>
                  </a>
                  {exam.videoStatus === 'active' ? (
                    <a
                      href={exam.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${exam.id}-video-btn`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        background: exam.color,
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        textDecoration: 'none',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      <Video size={16} />
                      Video Course
                      <span style={{ 
                        background: 'rgba(255,255,255,0.2)', 
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>$29.99/mo</span>
                    </a>
                  ) : (
                    <div
                      data-testid={`${exam.id}-video-btn`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        background: '#e2e8f0',
                        color: '#64748b',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      <Video size={16} />
                      Video Course
                      <span style={{ 
                        background: 'rgba(0,0,0,0.1)', 
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        fontSize: '0.7rem'
                      }}>Coming Soon - {exam.videoPrice}</span>
                    </div>
                  )}
                  <p style={{
                    fontSize: '0.7rem',
                    color: '#94a3b8',
                    marginTop: '4px',
                    lineHeight: '1.4'
                  }}>
                    {exam.videoStatus === 'active' 
                      ? 'Video includes mock exams & question bank. Cancel anytime - no fixed period'
                      : 'One-time payment. Video includes online book, lectures, podcast, games & mock exams'
                    }
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @media (max-width: 1100px) {
            .foundational-grid {
              grid-template-columns: repeat(3, 1fr) !important;
            }
          }
          @media (max-width: 768px) {
            .foundational-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          @media (max-width: 500px) {
            .foundational-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      {/* Accelerated Pathway - Interactive Books Section */}
      <section style={{
        padding: '64px 20px',
        background: '#f1f5f9'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '8px'
            }}>
              Accelerated Pathway — Interactive Books
            </h2>
            <p style={{
              fontSize: '1rem',
              color: '#475569',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Fast-track your prep with interactive study guides, then reinforce with video courses
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px'
          }} className="accelerated-grid">
            {[
              {
                id: 'cire',
                title: 'CIRE',
                fullName: 'Canadian Investment Regulatory Exam',
                color: '#1e40af',
                interactiveLink: '/books/cire-interactive',
                videoLink: 'https://vittafinance.mykajabi.com/offers/5Tzz73ay/checkout',
                interactivePrice: '$34.99'
              },
              {
                id: 'rse',
                title: 'RSE',
                fullName: 'Retail Securities Exam',
                color: '#166534',
                interactiveLink: '/books/rse-interactive',
                videoLink: 'https://vittafinance.mykajabi.com/offers/2kzQbJim/checkout',
                interactivePrice: '$49.99'
              },
              {
                id: 'derivatives',
                title: 'Derivatives',
                fullName: 'CIRO Derivatives Exam',
                color: '#92400e',
                interactiveLink: '/books/derivatives-interactive',
                videoLink: 'https://vittafinance.mykajabi.com/offers/K52zhpSs/checkout',
                interactivePrice: '$49.99'
              },
              {
                id: 'ise',
                title: 'ISE',
                fullName: 'Institutional Securities Exam',
                color: '#7c3aed',
                interactiveLink: '/books/ise-interactive',
                videoLink: 'https://vittafinance.mykajabi.com/offers/W6ezafud/checkout',
                interactivePrice: '$49.99'
              }
            ].map((exam) => (
              <div key={exam.id} style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px 20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                textAlign: 'center'
              }}>
                <h3 style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: exam.color,
                  marginBottom: '4px'
                }}>{exam.title}</h3>
                <p style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  marginBottom: '16px',
                  lineHeight: '1.3'
                }}>{exam.fullName}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <a
                    href={exam.interactiveLink}
                    data-testid={`${exam.id}-interactive-btn`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px 14px',
                      background: exam.color,
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <Brain size={16} />
                    Interactive Book
                    <span style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}>{exam.interactivePrice}</span>
                  </a>
                  <a
                    href={exam.videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${exam.id}-accel-video-btn`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px 14px',
                      background: 'white',
                      color: exam.color,
                      border: `2px solid ${exam.color}`,
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    <Video size={16} />
                    Video Course
                    <span style={{ 
                      background: '#f1f5f9', 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      color: '#475569'
                    }}>$29.99/mo</span>
                  </a>
                  <p style={{
                    fontSize: '0.7rem',
                    color: '#64748b',
                    marginTop: '4px',
                    lineHeight: '1.4'
                  }}>
                    Video includes mock exams & question bank. Cancel anytime - no fixed period
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) {
            .accelerated-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          @media (max-width: 500px) {
            .accelerated-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      {/* Features Section */}
      <section style={{
        padding: '80px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <h2 className="heading-3" style={{
            textAlign: 'center',
            marginBottom: '50px',
            fontSize: 'clamp(1.75rem, 3vw, 2.5rem)'
          }}>
            Why Choose Vitta Finance?
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                icon: <BookOpen size={36} color="var(--brand-primary)" />,
                title: 'Printed Books on Amazon',
                description: 'Comprehensive study guides, mock exams, and quick review books available on Amazon.',
                image: '/images/features/books.jpg'
              },
              {
                icon: <Video size={36} color="var(--brand-primary)" />,
                title: 'Instructor Videos & Podcast',
                description: 'Learn from expert instructors with comprehensive video lessons and on-the-go podcast episodes.',
                image: '/images/features/video-podcast.jpg'
              },
              {
                icon: <Brain size={36} color="var(--brand-primary)" />,
                title: 'Mind Maps & AI Tutor',
                description: '24/7 AI tutor trained on CIRO syllabus and interactive mind maps for faster learning.',
                image: '/images/features/ai-tutor.jpg'
              },
              {
                icon: <Award size={36} color="var(--brand-primary)" />,
                title: 'Practice Questions',
                description: 'Hundreds of exam-style questions with detailed explanations to build confidence.',
                image: '/images/features/practice-questions.jpg'
              },
              {
                icon: <CheckCircle size={36} color="var(--brand-primary)" />,
                title: 'Full Mock Exams',
                description: 'Simulate real exam conditions with timed practice tests that mirror CIRO format.',
                image: '/images/features/mock-exams.jpg'
              },
              {
                icon: <GraduationCap size={36} color="var(--brand-primary)" />,
                title: 'Expert Instructors',
                description: 'Learn from seasoned professionals with over 40 years of combined industry and teaching expertise.',
                image: '/images/features/expert-instructors.jpg'
              }
            ].map((feature, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                {/* Feature Image */}
                <div style={{
                  height: '180px',
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: 'var(--neutral-light)'
                }}>
                  <img 
                    src={feature.image}
                    alt={feature.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                <div style={{ padding: '24px' }}>
                  <div style={{ marginBottom: '16px', color: 'var(--brand-primary)' }}>
                    {feature.icon}
                  </div>
                  <h3 className="heading-5" style={{ marginBottom: '12px' }}>
                    {feature.title}
                  </h3>
                  <p className="body-small">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '80px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <h2 className="heading-3" style={{
            textAlign: 'center',
            marginBottom: '16px',
            fontSize: 'clamp(1.25rem, 4vw, 2rem)',
            padding: '0 16px'
          }}>
            Trusted by Thousands of Canadian Finance Professionals
          </h2>
          <p className="body-medium" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto 48px',
            padding: '0 16px',
            fontSize: 'clamp(0.875rem, 2vw, 1.125rem)'
          }}>
            Join the growing community of CIRO-certified professionals across Canada
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '24px',
            textAlign: 'center'
          }}>
            {[
              { number: '40+', label: 'Years Combined Experience' },
              { number: '1,000+', label: 'Practice Questions' },
              { number: '45-50 Hrs', label: 'Video Lectures per Exam' },
              { number: '5+', label: 'Full Mock Exams' },
              { number: '24/7', label: 'AI Tutor Support' },
              { number: '700+', label: 'Pages of Study Material' }
            ].map((stat, index) => (
              <div key={index}>
                <h3 style={{ 
                  marginBottom: '8px',
                  color: 'var(--brand-primary)',
                  fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                  fontWeight: 900
                }}>
                  {stat.number}
                </h3>
                <p style={{
                  fontSize: 'clamp(0.75rem, 2vw, 0.95rem)',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.4'
                }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Context Section - GEO Optimization */}
      <section style={{
        padding: '48px 0',
        backgroundColor: 'var(--brand-primary)',
        color: 'white'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center',
            padding: '0 8px'
          }}>
            <h2 style={{ 
              fontSize: 'clamp(1.25rem, 4vw, 2rem)', 
              fontWeight: 700, 
              marginBottom: '20px',
              color: 'white',
              lineHeight: 1.3
            }}>
              CIRO Certifications: The Gateway to Canadian Finance Careers
            </h2>
            <p style={{ 
              fontSize: 'clamp(0.875rem, 2.5vw, 1.1rem)', 
              lineHeight: 1.7, 
              marginBottom: '24px',
              opacity: 0.95
            }}>
              The Canadian Investment Regulatory Organization (CIRO) oversees investment dealers and mutual fund dealers across Canada. 
              CIRO certification exams—including CIRE, RSE, ISE, and Derivatives—are mandatory requirements for professionals 
              working in the Canadian securities industry.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginTop: '24px'
            }}>
              <div style={{ 
                padding: '16px 8px', 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                borderRadius: '12px' 
              }}>
                <p style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 900, color: 'var(--gold-primary)' }}>4</p>
                <p style={{ fontSize: 'clamp(0.7rem, 2vw, 0.9rem)', opacity: 0.9 }}>CIRO Exams</p>
              </div>
              <div style={{ 
                padding: '16px 8px', 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                borderRadius: '12px' 
              }}>
                <p style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 900, color: 'var(--gold-primary)' }}>CIRO</p>
                <p style={{ fontSize: 'clamp(0.7rem, 2vw, 0.9rem)', opacity: 0.9 }}>Sets Pass Score</p>
              </div>
              <div style={{ 
                padding: '16px 8px', 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                borderRadius: '12px' 
              }}>
                <p style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 900, color: 'var(--gold-primary)' }}>$29.99</p>
                <p style={{ fontSize: 'clamp(0.7rem, 2vw, 0.9rem)', opacity: 0.9 }}>Per Month</p>
              </div>
            </div>
            <Link to="/exam-comparison" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '24px',
              padding: '12px 24px',
              backgroundColor: 'var(--gold-primary)',
              color: 'white',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 'clamp(0.85rem, 2vw, 1rem)'
            }}>
              Compare All CIRO Exams <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Explore Individual Exam Prep */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--neutral-light)'
      }}>
        <div className="container">
          <h2 className="heading-3" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Explore Individual Exam Prep
          </h2>
          <p className="body-medium" style={{ marginBottom: '60px', color: 'var(--text-secondary)', textAlign: 'center' }}>
            Detailed information about each CIRO certification exam
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: 'CIRE Exam',
                icon: <BookOpen size={40} color="var(--brand-primary)" />,
                desc: 'Canadian Investment Regulatory Exam for RR/IR registration',
                link: '/cire-exam',
                stats: '110 questions • 2 hours • 9 elements',
                image: '/images/courses/cire-exam.jpg'
              },
              {
                title: 'RSE Exam',
                icon: <Award size={40} color="var(--brand-primary)" />,
                desc: 'Retail Securities Exam for client-facing advisors',
                link: '/rse-exam',
                stats: '120 questions • 3 hours • KYC/KYP',
                image: '/images/courses/rse-exam.jpg'
              },
              {
                title: 'ISE Exam',
                icon: <Users size={40} color="var(--brand-primary)" />,
                desc: 'Institutional Securities for corporate finance professionals',
                link: '/ise-exam',
                stats: '100 MCQs • 2.5 hours',
                image: '/images/courses/ise-exam.jpg'
              },
              {
                title: 'Derivatives',
                icon: <CheckCircle size={40} color="var(--brand-primary)" />,
                desc: 'Options, futures, and derivative trading strategies',
                link: '/derivatives-exam',
                stats: '120 questions • 3 hours',
                image: '/images/courses/derivatives-exam.jpg'
              },
              {
                title: 'Traders Exam',
                icon: <Target size={40} color="#0d9488" />,
                desc: 'CIRO Traders Licensing Examination for licensed traders',
                link: '/traders-exam',
                stats: '100 questions • 2 hours • 10 elements',
                image: '/images/courses/traders-exam.jpg',
                isNew: true
              }
            ].map((exam, idx) => (
              <Link 
                key={idx} 
                to={exam.link}
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '2px solid var(--border-light)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }} className="card-hover-effect">
                  {/* NEW Badge */}
                  {exam.isNew && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      zIndex: 10
                    }}>NEW</div>
                  )}
                  {/* Exam Image */}
                  <div style={{
                    height: '220px',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: 'var(--neutral-light)'
                  }}>
                    <img 
                      src={exam.image}
                      alt={exam.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                  <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: '16px', color: exam.title === 'Traders Exam' ? '#0d9488' : 'var(--brand-primary)' }}>
                      {exam.icon}
                    </div>
                    <h3 className="heading-4" style={{ marginBottom: '12px' }}>{exam.title}</h3>
                    <p className="body-small" style={{ marginBottom: '16px', flex: 1 }}>{exam.desc}</p>
                    <p className="caption" style={{ 
                      color: exam.title === 'Traders Exam' ? '#0d9488' : 'var(--brand-primary)', 
                      marginBottom: '16px',
                      padding: '8px 12px',
                      backgroundColor: exam.title === 'Traders Exam' ? '#ccfbf1' : 'var(--neutral-light)',
                      borderRadius: '4px'
                    }}>
                      {exam.stats}
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: exam.title === 'Traders Exam' ? '#0d9488' : 'var(--brand-primary)',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}>
                      Learn More <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section style={{
        padding: '80px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <h2 className="heading-3" style={{
            textAlign: 'center',
            marginBottom: '50px',
            fontSize: 'clamp(1.75rem, 3vw, 2.5rem)'
          }}>
            What Our Students Say
          </h2>
          
          {/* Scrolling Container */}
          <div style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--brand-primary) var(--border-light)'
          }}>
            <div style={{
              display: 'flex',
              gap: '24px',
              paddingBottom: '20px',
              minWidth: 'min-content'
            }}>
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '32px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)',
                  minWidth: '320px',
                  maxWidth: '320px',
                  flexShrink: 0
                }}>
                  <p style={{ 
                    fontSize: '0.95rem',
                    marginBottom: '20px',
                    fontStyle: 'italic',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.6
                  }}>
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p style={{ 
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      marginBottom: '4px',
                      fontSize: '0.95rem'
                    }}>
                      {testimonial.name}
                    </p>
                    <p style={{
                      fontSize: '0.85rem',
                      color: 'var(--text-muted)'
                    }}>
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Scroll Indicator */}
          <p style={{
            textAlign: 'center',
            marginTop: '20px',
            fontSize: '0.875rem',
            color: 'var(--text-muted)'
          }}>
            ← Scroll to see more testimonials →
          </p>
        </div>
        
        <style>{`
          /* Custom scrollbar styling */
          section:has(.container) div::-webkit-scrollbar {
            height: 8px;
          }
          section:has(.container) div::-webkit-scrollbar-track {
            background: var(--border-light);
            border-radius: 10px;
          }
          section:has(.container) div::-webkit-scrollbar-thumb {
            background: var(--brand-primary);
            border-radius: 10px;
          }
          section:has(.container) div::-webkit-scrollbar-thumb:hover {
            background: var(--brand-hover);
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>
            Ready to Pass Your CIRO Exam?
          </h2>
          <p style={{ 
            fontSize: '1.25rem',
            lineHeight: 1.7,
            marginBottom: '40px',
            maxWidth: '700px',
            margin: '0 auto 40px',
            color: 'white',
            fontWeight: 500
          }}>
            Join thousands of successful candidates who achieved their certification with Vitta Finance Academy
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link to="/courses" className="btn-gold">
              Browse Courses <ArrowRight size={20} />
            </Link>
            <Link to="/contact" className="btn-secondary" style={{
              backgroundColor: 'white',
              color: 'var(--brand-primary)',
              borderColor: 'white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
